/**
 * Sai activation trigger evaluation. Activations are full-screen pauses
 * driven by gameplay events.
 *
 * To avoid spam, each activation fires at most once. The caller tracks fired
 * IDs separately (e.g. in the UI store or a server flag) — this module
 * concerns itself only with "does it fire now?".
 */
import type { ActivationTrigger, Epoch, GameState, SaiActivation } from './types.js';

export interface ActivationContext {
  readonly state: GameState;
  /**
   * The event that just happened. Some triggers fire on transitions, not on
   * arbitrary state; this lets us be precise.
   */
  readonly event: ActivationEvent;
}

export type ActivationEvent =
  | { readonly kind: 'tap'; readonly newTotalTaps: number }
  | {
      readonly kind: 'crystallization';
      readonly didAdvanceEpoch: boolean;
      readonly newEpoch: Epoch;
    }
  | { readonly kind: 'goldenSparkCaught' }
  | { readonly kind: 'firstBreathCycle' }
  | { readonly kind: 'session_start'; readonly hoursSincePrevious: number }
  | { readonly kind: 'lesson_unlocked'; readonly lessonId: string };

/**
 * Returns the first activation that should fire given the current event, or
 * null if no activation matches. We return the first match to avoid bursting
 * multiple modal panels at once.
 */
export function selectActivation(
  catalog: readonly SaiActivation[],
  alreadyFired: ReadonlySet<string>,
  ctx: ActivationContext,
): SaiActivation | null {
  for (const activation of catalog) {
    if (alreadyFired.has(activation.id)) {
      continue;
    }
    if (triggerMatches(activation.trigger, ctx)) {
      return activation;
    }
  }
  return null;
}

function triggerMatches(trigger: ActivationTrigger, ctx: ActivationContext): boolean {
  const { event } = ctx;

  switch (trigger.kind) {
    case 'firstTap':
      return event.kind === 'tap' && event.newTotalTaps === 1;

    case 'tapCount':
      return event.kind === 'tap' && event.newTotalTaps === trigger.value;

    case 'firstCrystallization':
      return event.kind === 'crystallization' && ctx.state.stats.totalCrystallizations === 1;

    case 'enterEpoch':
      return (
        event.kind === 'crystallization' &&
        event.didAdvanceEpoch &&
        event.newEpoch === trigger.epoch
      );

    case 'firstBreathCycle':
      return event.kind === 'firstBreathCycle';

    case 'returnAfterHours':
      return event.kind === 'session_start' && event.hoursSincePrevious >= trigger.value;

    case 'firstGoldenSpark':
      return event.kind === 'goldenSparkCaught' && ctx.state.stats.totalGoldenSparksCaught === 1;

    case 'lessonUnlocked':
      return event.kind === 'lesson_unlocked' && event.lessonId === trigger.lessonId;

    default:
      return assertNever(trigger);
  }
}

function assertNever(value: never): never {
  throw new Error(`Unhandled activation trigger: ${JSON.stringify(value)}`);
}
